import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { api } from "../../../api/apiService";
import { getImageUrl } from "../../../config/utils";
import { emitter } from "../../../config/emitter";


const schema = yup.object().shape({
  first_name: yup.string().required("First Name is required"),
  last_name: yup.string().required("Last Name is required"),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  image: yup.mixed().nullable(),
});

const Profile = () => {
  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    email: "",
    image: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: profile,
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoader(true);
    try {
      const response = await api.get("profile");
      const { first_name, last_name, email, image } = response.data.data;
      const updatedProfile = {
        first_name,
        last_name,
        email,
        image: image || null,
      };
      setProfile(updatedProfile);
      reset(updatedProfile);
    } catch (error) {
      toast.error("Failed to fetch profile data.");
      console.error(error.response);
    } finally {
      setLoader(false);
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        setPreviewImage(URL.createObjectURL(file));
        setUploadedFile(file);
        setValue("image", file, { shouldValidate: true });
      } else {
        toast.error("Please upload a valid image file.");
      }
    }
  };

  const removePreviewImage = () => {
    setPreviewImage(null);
    setUploadedFile(null);
    setValue("image", null, { shouldValidate: true });
  };

  const updateProfile = async (data) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("first_name", data.first_name);
    formData.append("last_name", data.last_name);
    formData.append("email", data.email);

    if (uploadedFile) {
      formData.append("image", uploadedFile);
    }

    try {
      const response = await api.post("profile/update", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      emitter.emit("image", response.data.data.profile.image);
      emitter.emit("updatedProfile", response.data.data);

      toast.success(response.data.message);
    } catch (error) {
      if (error.data && error.data.errors) {
        error.data.errors.forEach(err => {
          toast.error(err);
        });
      } else {
        toast.error("Failed to update profile.");
      }

    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main id="main" className="main">
      <div className="pagetitle">
        <h1>Profile</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/home">Home</Link>
            </li>
            <li className="breadcrumb-item active">Profile</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Update Profile</h5>
                {loader ? (
                  <div className="text-center my-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(updateProfile)}>
                    <div className="mb-3">
                      <div className="image-preview-wrapper">
                        {previewImage ? (
                          <>
                            <button
                              type="button"
                              onClick={removePreviewImage}
                              className="remove-preview-btn"
                              aria-label="Remove Image"
                            >
                              &times;
                            </button>
                            <img
                              src={previewImage}
                              alt="Preview"
                              style={{ maxWidth: "200px" }}
                              className="profile-picture"
                            />
                          </>
                        ) : (
                          <img
                            src={
                              profile.image
                                ? getImageUrl(profile.image)
                                : "/backend/assets/img/image-box.svg"
                            }
                            alt="Profile"
                            className="profile-picture"
                            style={{ maxWidth: "200px" }}
                          />
                        )}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <label htmlFor="first_name" className="form-label">
                          First Name
                        </label>
                        <input
                          type="text"
                          className={`form-control ${errors.first_name ? "is-invalid" : ""
                            }`}
                          {...register("first_name")}
                        />
                        <div className="invalid-feedback">
                          {errors.first_name?.message}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label htmlFor="last_name" className="form-label">
                          Last Name
                        </label>
                        <input
                          type="text"
                          className={`form-control ${errors.last_name ? "is-invalid" : ""
                            }`}
                          {...register("last_name")}
                        />
                        <div className="invalid-feedback">
                          {errors.last_name?.message}
                        </div>
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-md-8">
                        <label htmlFor="email" className="form-label">
                          Email
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          value={profile.email}
                          disabled
                        />
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-md-8">
                        <label htmlFor="image" className="form-label">
                          Profile Image
                        </label>
                        <input
                          type="file"
                          className={`form-control ${errors.image ? "is-invalid" : ""
                            }`}
                          {...register("image")}
                          onChange={handleImageUpload}
                        />
                        <div className="invalid-feedback">
                          {errors.image?.message}
                        </div>
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary mt-3"
                      disabled={isLoading}
                    >
                      Update Profile
                      {isLoading && (
                        <span
                          className="spinner-border spinner-border-sm ms-2"
                          role="status"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Profile;
